// Wave UX-2.2 #6 (2026-05-23) — 외부 바이어 알림 센터 (/buyer/me/notifications).
// 누적 알림 — bell (urgent live) 과 별개로 history 진입점.
//
// 데이터 소스:
//   GET /me/bids     → myBid.status='winner' → 🏆 낙찰 / 'rejected' → 미낙찰
//   GET /me/favorites + /me/bids 의 ending-soon → ⏰ 마감 임박
//
// 읽음 상태: localStorage 'autodex_buyer_notif_read' (Set of notification IDs).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { useT } from '../../i18n/I18nProvider';
import { buildEndingSoonList, formatRemaining } from './endingSoon';

var READ_KEY = 'autodex_buyer_notif_read';

function loadReadSet() {
  try {
    var raw = localStorage.getItem(READ_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (e) { return {}; }
}
function saveReadSet(s) { try { localStorage.setItem(READ_KEY, JSON.stringify(s)); } catch (e) {} }

// notification id format: '<type>:<auctionId>' (멱등 — 같은 이벤트 중복 없음)
function notifId(type, auctionId) { return type + ':' + auctionId; }

function AuctionBuyerNotifications() {
  var t = useT();
  var navigate = useNavigate();
  var [items, setItems] = useState([]);
  var [tab, setTab] = useState('all');
  var [readSet, setReadSet] = useState(loadReadSet());
  var [loaded, setLoaded] = useState(false);

  var load = useCallback(function() {
    var hitAuth = false;
    function checkAuth(r) {
      if (isUnauthorized(r)) { hitAuth = true; throw new Error('unauthorized'); }
      return r.json();
    }
    Promise.allSettled([
      authBuyerFetch(API + '/buyer-app/me/favorites', {}).then(checkAuth),
      authBuyerFetch(API + '/buyer-app/me/bids', {}).then(checkAuth),
    ]).then(function(results) {
      if (hitAuth) { handleUnauthorized(navigate); return; }
      var favRes = results[0].status === 'fulfilled' ? results[0].value : null;
      var bidsRes = results[1].status === 'fulfilled' ? results[1].value : null;
      var favorites = (favRes && favRes.success && favRes.data && favRes.data.auctions) || [];
      var bids = (bidsRes && bidsRes.success && bidsRes.data && bidsRes.data.bids) || [];

      var list = [];
      var now = Date.now();

      // 1. winner / rejected (from bids)
      bids.forEach(function(a) {
        if (!a || !a.myBid) return;
        var car = a.car || {};
        var title = car.model || a.title || '';
        if (a.myBid.status === 'winner') {
          list.push({
            id: notifId('win', a.id),
            type: 'win', auctionId: a.id, title: title, carNumber: car.number || '',
            body: t('notifWinBody', { title: title || car.number || a.id }),
            at: a.myBid.updatedAt || a.endsAt || '',
          });
        } else if (a.myBid.status === 'rejected') {
          list.push({
            id: notifId('reject', a.id),
            type: 'reject', auctionId: a.id, title: title, carNumber: car.number || '',
            body: t('notifRejectBody', { title: title || car.number || a.id }),
            at: a.myBid.updatedAt || a.endsAt || '',
          });
        }
      });

      // 2. ending soon (1h/10m) — shared helper
      var ending = buildEndingSoonList(favorites, bids, now);
      ending.forEach(function(it) {
        list.push({
          id: notifId('ending:' + it.bucket, it.id),
          type: 'ending', bucket: it.bucket,
          auctionId: it.id, title: it.title, carNumber: it.carNumber,
          body: t('notifEndingBody', { title: it.title || it.carNumber || it.id, remain: formatRemaining(it.endsAt, t, now) }),
          at: it.endsAt,
        });
      });

      // 시간 역순 (최신 우선) — at 없으면 마지막
      list.sort(function(a, b) {
        var ta = a.at ? new Date(a.at).getTime() : 0;
        var tb = b.at ? new Date(b.at).getTime() : 0;
        return tb - ta;
      });

      setItems(list);
      setLoaded(true);
    });
  }, [navigate, t]);

  useEffect(function(){ load(); }, [load]);

  function markRead(id) {
    var next = Object.assign({}, readSet);
    next[id] = Date.now();
    setReadSet(next);
    saveReadSet(next);
  }
  function markAllRead() {
    var next = Object.assign({}, readSet);
    items.forEach(function(it){ next[it.id] = Date.now(); });
    setReadSet(next);
    saveReadSet(next);
  }
  function isUnread(id) { return !readSet[id]; }

  var filtered = tab === 'unread' ? items.filter(function(it){ return isUnread(it.id); }) : items;
  var unreadCount = items.filter(function(it){ return isUnread(it.id); }).length;

  function typeBadgeColor(type, bucket) {
    if (type === 'win') return { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0' };
    if (type === 'reject') return { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
    if (type === 'ending') {
      return bucket === '10m'
        ? { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' }
        : { bg: '#fffbeb', color: '#92400e', border: '#fde68a' };
    }
    return { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' };
  }
  function typeLabel(type) {
    if (type === 'win') return t('notifTypeWin');
    if (type === 'reject') return t('notifTypeReject');
    if (type === 'ending') return t('notifTypeEnding');
    return '';
  }

  function onClickItem(it) {
    markRead(it.id);
    if (it.auctionId) navigate('/buyer/auctions/' + it.auctionId);
  }

  return (
    <AuctionBuyerLayout activeKey={null}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px 80px', }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{t('notifCenterTitle')}</h2>
          {unreadCount > 0 && (
            <button type="button" onClick={markAllRead}
              style={{
                background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8,
                padding: '8px 14px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: '#475569',              }}>
              {t('notifMarkAllRead')}
            </button>
          )}
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid #e2e8f0', marginBottom: 16 }}>
          {[
            { key: 'all', labelKey: 'notifTabAll', count: items.length },
            { key: 'unread', labelKey: 'notifTabUnread', count: unreadCount },
          ].map(function(td){
            var active = tab === td.key;
            return (
              <button key={td.key} type="button" onClick={function(){ setTab(td.key); }}
                style={{
                  padding: '10px 16px', background: 'transparent', border: 'none',
                  borderBottom: active ? '2.5px solid #1B3A5C' : '2.5px solid transparent',
                  fontSize: 13, fontWeight: active ? 700 : 600,
                  color: active ? '#1B3A5C' : '#64748b',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, marginBottom: '-1.5px',
                }}>
                <span>{t(td.labelKey)}</span>
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  background: active ? '#1B3A5C' : '#e2e8f0',
                  color: active ? 'white' : '#64748b',
                  padding: '2px 8px', borderRadius: 999, minWidth: 18, textAlign: 'center',
                }}>{td.count}</span>
              </button>
            );
          })}
        </div>

        {!loaded ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            {t('commonLoading')}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="🔔" title={t('notifCenterEmpty')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(function(it) {
              var unread = isUnread(it.id);
              var c = typeBadgeColor(it.type, it.bucket);
              return (
                <div key={it.id} onClick={function(){ onClickItem(it); }}
                  className="buyer-item-lift"
                  style={{
                    background: unread ? '#f8fafc' : 'white',
                    border: '1px solid ' + (unread ? '#cbd5e1' : '#e2e8f0'),
                    borderLeft: unread ? '3px solid #3b82f6' : '1px solid #e2e8f0',
                    borderRadius: 10, padding: 14, cursor: 'pointer',
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{
                        display: 'inline-block',
                        background: c.bg, color: c.color, border: '1px solid ' + c.border,
                        padding: '3px 10px', borderRadius: 999,
                        fontSize: 11, fontWeight: 700, marginBottom: 6,
                      }}>{typeLabel(it.type)}</span>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>
                        {it.body}
                      </div>
                      {it.carNumber && (
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{it.carNumber}</div>
                      )}
                    </div>
                    {unread && <span style={{ width: 8, height: 8, borderRadius: 999, background: '#3b82f6', flexShrink: 0, marginTop: 6 }} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerNotifications;
